import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ImageUp, Loader2, RotateCcw, Save } from 'lucide-react';
import {
  enregistrerPersonnalisation,
  fetchPersonnalisation,
  televerserLogo,
} from '../services/api';
import { palette } from '../hooks/usePersonnalisation';

const VALIDE = /^#[0-9a-fA-F]{6}$/;

/**
 * Apparence du site : couleurs d'accentuation et logo.
 *
 * Le comité choisit une couleur par famille ; les onze nuances dont Tailwind a
 * besoin s'en déduisent. L'aperçu les montre toutes, parce qu'une couleur
 * séduisante au cran 600 peut devenir illisible en fond clair, et qu'il vaut
 * mieux s'en apercevoir ici que sur le site.
 */
export default function Personnalisation() {
  const queryClient = useQueryClient();
  const champFichier = useRef<HTMLInputElement>(null);

  const { data, isPending } = useQuery({
    queryKey: ['personnalisation'],
    queryFn: fetchPersonnalisation,
  });

  const perso = data?.personnalisation;

  const [primaire, setPrimaire] = useState('#16a34a');
  const [secondaire, setSecondaire] = useState('#dc2626');
  const [notice, setNotice] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!perso) return;
    setPrimaire(perso.couleurPrimaire);
    setSecondaire(perso.couleurSecondaire);
  }, [perso]);

  const rafraichir = () => {
    // Le site relit ses couleurs et son logo : le changement se voit tout de
    // suite, sans avoir à recharger la page.
    queryClient.invalidateQueries({ queryKey: ['personnalisation'] });
  };

  const enregistrer = useMutation({
    mutationFn: () => enregistrerPersonnalisation({ couleurPrimaire: primaire, couleurSecondaire: secondaire }),
    onSuccess: () => {
      setNotice('Couleurs enregistrées.');
      setErreur(null);
      rafraichir();
    },
    onError: (e: Error) => {
      setErreur(e.message);
      setNotice(null);
    },
  });

  const envoyerLogo = useMutation({
    mutationFn: (fichier: File) => televerserLogo(fichier),
    onSuccess: () => {
      setNotice('Logo remplacé, sur le site comme sur les documents.');
      setErreur(null);
      rafraichir();
    },
    onError: (e: Error) => {
      setErreur(e.message);
      setNotice(null);
    },
  });

  const invalide = !VALIDE.test(primaire) || !VALIDE.test(secondaire);
  const inchange =
    perso && primaire === perso.couleurPrimaire && secondaire === perso.couleurSecondaire;

  if (isPending) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
      </div>
    );
  }

  // Le titre et la carte sont portés par SectionParametres.
  return (
    <div className="space-y-5">
      {notice && (
        <p className="text-sm rounded-xl px-3 py-2 bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          {notice}
        </p>
      )}
      {erreur && (
        <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700">{erreur}</p>
      )}

      {/* Couleurs */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChoixCouleur
            titre="Couleur d'accentuation"
            aide="Boutons, liens, éléments actifs. C'est elle qu'on voit partout."
            valeur={primaire}
            defaut={perso?.couleurPrimaireDefaut}
            onChange={setPrimaire}
          />
          <ChoixCouleur
            titre="Couleur secondaire"
            aide="Accents ponctuels et rappels du logo."
            valeur={secondaire}
            defaut={perso?.couleurSecondaireDefaut}
            onChange={setSecondaire}
          />
        </div>

        <button
          onClick={() => enregistrer.mutate()}
          disabled={enregistrer.isPending || invalide || inchange === true}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {enregistrer.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Enregistrer les couleurs
        </button>
      </div>

      {/* Logo */}
      <div className="pt-5 border-t border-gray-100 dark:border-gray-800 space-y-3">
        <div>
          <p className="font-medium text-gray-900 dark:text-gray-100">Logo du site</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Il apparaît dans l'en-tête, sur l'écran d'accueil de l'application et en tête des
            devis et factures. PNG, JPEG ou WebP, 5 Mo au plus ; la transparence est conservée.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="w-20 h-20 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white flex items-center justify-center overflow-hidden shrink-0">
            {perso?.logoUrl && (
              <img src={perso.logoUrl} alt="Logo actuel" className="w-full h-full object-contain" />
            )}
          </div>

          <div>
            <input
              ref={champFichier}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const fichier = e.target.files?.[0];
                if (fichier) envoyerLogo.mutate(fichier);
                e.target.value = '';
              }}
            />
            <button
              onClick={() => champFichier.current?.click()}
              disabled={envoyerLogo.isPending}
              className="btn-outline inline-flex items-center gap-2 disabled:opacity-50"
            >
              {envoyerLogo.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ImageUp className="w-4 h-4" />
              )}
              Remplacer le logo
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              Le remplacement vaut aussitôt pour les documents comptables.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Un sélecteur de couleur et l'échelle qu'il produit.
 */
function ChoixCouleur({
  titre,
  aide,
  valeur,
  defaut,
  onChange,
}: {
  titre: string;
  aide: string;
  valeur: string;
  defaut?: string;
  onChange: (v: string) => void;
}) {
  const nuances = VALIDE.test(valeur) ? palette(valeur) : null;

  return (
    <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-4 space-y-3">
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{titre}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{aide}</p>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="color"
          value={VALIDE.test(valeur) ? valeur : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          aria-label={titre}
          className="w-12 h-11 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent cursor-pointer shrink-0"
        />
        <input
          className="input font-mono"
          value={valeur}
          onChange={(e) => onChange(e.target.value.trim())}
          placeholder="#16a34a"
        />
        {defaut && valeur.toLowerCase() !== defaut && (
          <button
            type="button"
            onClick={() => onChange(defaut)}
            title="Revenir à la couleur d'origine"
            aria-label="Revenir à la couleur d'origine"
            className="p-2 rounded-lg text-gray-500 hover:text-primary-700 hover:bg-primary-50 dark:text-gray-400 dark:hover:bg-gray-700 shrink-0"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
      </div>

      {nuances ? (
        <div className="flex rounded-lg overflow-hidden border border-gray-100 dark:border-gray-800">
          {Object.entries(nuances).map(([cran, hex]) => (
            <div
              key={cran}
              title={`${cran} — ${hex}`}
              className="h-7 flex-1"
              style={{ backgroundColor: hex }}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-red-600">
          Format attendu : six caractères hexadécimaux précédés d'un dièse.
        </p>
      )}
    </div>
  );
}
